<template>
  <div>
    <v-alert
      type="info"
      outlined
      border="left"
      colored-border
      elevation="1"
      icon="mdi-calculator-variant-outline"
      class="mb-4"
    >
      <div class="font-weight-bold">
        ผลการเลื่อนค่าตอบแทนพนักงานราชการ — ส่วนกลางเป็นผู้ประมวลผล
        (หน่วยงานดูได้อย่างเดียว)
      </div>
      <div class="caption mt-1">
        วงเงินรวมไม่เกิน 4% ของฐานค่าตอบแทน × 12 เดือน · รายบุคคลไม่เกิน 6% ·
        ปัดเศษไม่ถึง 10 บาทเป็น 10 บาท · มีผล 1 ตุลาคม
      </div>
    </v-alert>

    <!-- ขั้นตอน -->
    <v-card outlined class="mb-4 pa-3">
      <div class="d-flex flex-wrap align-center" style="gap:6px">
        <template v-for="(s, i) in steps">
          <v-chip
            :key="'s' + i"
            small
            :color="s.done ? 'success' : s.current ? 'primary' : 'grey lighten-2'"
            :dark="s.done || s.current"
          >
            <v-icon x-small left>{{ s.done ? "mdi-check-circle" : s.icon }}</v-icon>
            {{ i + 1 }}. {{ s.text }}
          </v-chip>
          <v-icon v-if="i < steps.length - 1" :key="'a' + i" small color="grey">mdi-chevron-right</v-icon>
        </template>
      </div>
    </v-card>

    <v-row dense class="mb-2">
      <v-col cols="12" md="3">
        <v-select
          v-model="compYear"
          :items="years"
          label="ปีงบประมาณที่ประเมิน"
          outlined
          dense
          hide-details
          prepend-inner-icon="mdi-calendar-range"
          no-data-text="ยังไม่มีปีที่ส่วนกลางยืนยันผล"
          @change="load"
        />
      </v-col>
      <v-col cols="12" md="9" class="d-flex align-center">
        <span v-if="unit" class="subtitle-2">
          <v-icon small>mdi-school</v-icon>
          {{ unit.college_name }} ({{ unit.user_code }})
        </span>
      </v-col>
    </v-row>

    <v-progress-linear v-if="loading" indeterminate color="info" class="mb-3" />

    <v-alert v-if="!loading && loaded && !run" type="warning" outlined border="left" prominent>
      <div class="font-weight-bold">ส่วนกลางยังไม่ยืนยันผลการประมวลผล</div>
      <div class="caption">
        เมื่อส่วนกลางประมวลผลและยืนยันผลแล้ว ข้อมูลการเลื่อนค่าตอบแทนของหน่วยงานจะแสดงที่หน้านี้
        ระหว่างนี้กรุณาตรวจสอบผลการประเมิน (แบบ ปผ.พนร.) และข้อมูลการลา/สาย/ขาดให้ครบถ้วน
      </div>
    </v-alert>

    <template v-if="run && summary">
      <div class="caption grey--text mb-2">
        ผลการประเมินปีงบประมาณ {{ run.comp_year }} ({{ roundModeText(run.round_mode) }}) ·
        ส่วนกลางยืนยันผลเมื่อ {{ run.finalized_at }} · รอบประมวลผล #{{ run.run_id }}
      </div>

      <!-- สรุป -->
      <v-row dense class="mb-3">
        <v-col v-for="k in kpis" :key="k.label" cols="6" md="2">
          <div class="kpi" :style="{ borderLeftColor: k.color }">
            <div class="kpi-label">{{ k.label }}</div>
            <div class="kpi-value" :style="{ color: k.color }">{{ k.value }}</div>
            <div class="kpi-unit">{{ k.unit }}</div>
          </div>
        </v-col>
      </v-row>

      <!-- ตาราง -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
          <v-icon left>mdi-table-account</v-icon>
          รายละเอียดรายบุคคล
          <v-spacer />
          <v-btn-toggle v-model="filterMode" dense mandatory class="mr-2">
            <v-btn x-small value="all">ทั้งหมด ({{ details.length }})</v-btn>
            <v-btn x-small value="eligible">มีสิทธิ์ ({{ eligibleCount }})</v-btn>
            <v-btn x-small value="ineligible">ไม่มีสิทธิ์ ({{ details.length - eligibleCount }})</v-btn>
          </v-btn-toggle>
          <v-btn small outlined color="success" class="mr-1" @click="exportExcel">
            <v-icon small left>mdi-microsoft-excel</v-icon>Excel
          </v-btn>
          <v-btn small color="primary" @click="printForm">
            <v-icon small left>mdi-printer</v-icon>พิมพ์แบบรายงาน (ผอ.ลงนาม)
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="ค้นหาชื่อ / เลขบัตร / ตำแหน่ง"
            class="mb-2"
            style="max-width:360px"
          />
          <v-data-table
            :headers="headers"
            :items="filteredDetails"
            :search="search"
            dense
            fixed-header
            height="520px"
            disable-pagination
            hide-default-footer
            class="calc-table"
          >
            <template v-slot:item.order="{ item }">{{ item.order }}</template>
            <template v-slot:item.full_name="{ item }">
              <div class="font-weight-medium" style="white-space:nowrap">{{ item.full_name }}</div>
              <div class="caption grey--text">{{ item.name_postion }} · {{ item.name_type_group }}</div>
              <div class="caption info--text">{{ item.id_card }}</div>
            </template>
            <template v-slot:item.salary_s="{ item }">{{ money(item.salary_s) }}</template>
            <template v-slot:item.performance_score="{ item }">
              <span v-if="+item.performance_score > 0">
                {{ item.performance_score }}
                <div class="caption grey--text">รอบ {{ item.score_round }}</div>
              </span>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:item.eligible="{ item }">
              <v-chip x-small dark :color="+item.eligible ? 'success' : 'error'">
                {{ +item.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์" }}
              </v-chip>
              <div
                v-if="item.ineligible_reason"
                class="caption"
                :class="+item.eligible ? 'warning--text' : 'error--text'"
                style="max-width:200px;white-space:normal"
              >
                {{ item.ineligible_reason }}
              </div>
            </template>
            <template v-slot:item.individual_pct="{ item }">{{ pct(item, "individual_pct") }}</template>
            <template v-slot:item.management_pct="{ item }">{{ pct(item, "management_pct") }}</template>
            <template v-slot:item.actual_pct_raw="{ item }">{{ pct(item, "actual_pct_raw") }}</template>
            <template v-slot:item.actual_increase_raw="{ item }">
              <span class="info--text text--darken-2">
                {{ +item.eligible ? money2(item.actual_increase_raw) : "-" }}
              </span>
            </template>
            <template v-slot:item.final_rounded="{ item }">
              <span class="font-weight-bold primary--text">
                {{ +item.eligible ? money(item.final_rounded) : "-" }}
              </span>
              <div v-if="+item.capped" class="caption warning--text">ถึงขั้นสูงกลุ่มงาน</div>
              <div v-if="+item.pct_capped" class="caption warning--text">ตัดที่เพดาน 6%</div>
            </template>
            <template v-slot:item.actual_pct="{ item }">{{ pct(item, "actual_pct") }}</template>
            <template v-slot:item.new_salary="{ item }">
              <span class="font-weight-bold">{{ money(item.new_salary) }}</span>
            </template>
            <template v-slot:body.append>
              <tr class="grey lighten-3 font-weight-bold">
                <td colspan="3" class="text-right">รวม</td>
                <td class="text-right">{{ money(summary.total_salary) }}</td>
                <td colspan="4"></td>
                <td class="text-right">{{ money2(totalRaw) }}</td>
                <td class="text-right">{{ money(summary.distributed_month) }}</td>
                <td></td>
                <td class="text-right">{{ money(summary.new_salary_total) }}</td>
              </tr>
            </template>
          </v-data-table>
          <div class="caption grey--text mt-2">
            "เลื่อนจริงก่อนปัดเศษ" = ค่าตอบแทนเดิม × % เลื่อนจริงก่อนปัดเศษ (% รายบุคคล + % บริหารวงเงิน ไม่เกิน 6%)
            · "เลื่อนจริง" = หลังปัดเศษขึ้นหลักสิบ กระจายวงเงินคงเหลือ และควบคุมไม่ให้เกินวงเงิน/ขั้นสูงกลุ่มงาน
          </div>
        </v-card-text>
      </v-card>

      <!-- แนบแบบรับรอง -->
      <v-card outlined class="mb-4">
        <v-card-title class="subtitle-1 font-weight-bold grey lighten-4 py-2">
          <v-icon left>mdi-file-sign</v-icon>
          แนบแบบรายงานที่ผู้อำนวยการลงนามรับรอง
          <v-spacer />
          <v-chip v-if="cert" small dark :color="certColor(cert.cert_status)">
            {{ certText(cert.cert_status) }}
          </v-chip>
          <v-chip v-else small>ยังไม่แนบ</v-chip>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert v-if="cert && cert.cert_status === 'rejected'" type="error" dense outlined>
            ส่วนกลางส่งกลับ{{ cert.reviewed_at ? " เมื่อ " + cert.reviewed_at : "" }}:
            <strong>{{ cert.review_note }}</strong> — กรุณาแก้ไขและแนบใหม่
          </v-alert>
          <v-alert v-if="cert && cert.cert_status === 'accepted'" type="success" dense outlined>
            ส่วนกลางรับรองแบบรายงานแล้วเมื่อ {{ cert.reviewed_at }}
            <span v-if="cert.review_note"> — {{ cert.review_note }}</span>
          </v-alert>
          <div v-if="cert" class="mb-3">
            <v-icon small>mdi-paperclip</v-icon>
            {{ cert.file_name }} ({{ (cert.file_size / 1024).toFixed(0) }} KB)
            · ผู้ลงนาม {{ cert.director_name }} · แนบเมื่อ {{ cert.uploaded_at }}
            <v-btn x-small text color="primary" :loading="downloading" @click="openCert">
              <v-icon x-small left>mdi-eye</v-icon>เปิดดู
            </v-btn>
          </div>

          <v-row v-if="!cert || cert.cert_status !== 'accepted'" dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.director_name"
                label="ชื่อผู้อำนวยการผู้ลงนามรับรอง *"
                outlined
                dense
                hide-details="auto"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-file-input
                v-model="form.file"
                label="ไฟล์ที่ลงนามแล้ว (PDF/JPG/PNG ≤ 10MB) *"
                accept=".pdf,.jpg,.jpeg,.png"
                outlined
                dense
                show-size
                hide-details="auto"
                prepend-icon=""
                prepend-inner-icon="mdi-paperclip"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="form.college_note"
                label="หมายเหตุถึงส่วนกลาง (ถ้ามี)"
                outlined
                dense
                hide-details
                maxlength="500"
              />
            </v-col>
            <v-col cols="12" class="text-right">
              <v-btn
                color="success"
                :loading="uploading"
                :disabled="!form.file || !form.director_name"
                @click="uploadCert"
              >
                <v-icon left>mdi-upload</v-icon>{{ cert ? "แนบไฟล์ใหม่แทนไฟล์เดิม" : "ส่งแบบรายงาน" }}
              </v-btn>
            </v-col>
          </v-row>
          <div class="caption grey--text mt-2">
            หากพบข้อมูลไม่ถูกต้อง (เช่น ค่าตอบแทนเดิม ผลประเมิน วันลา) ให้ระบุในหมายเหตุ
            หรือแจ้งส่วนกลางก่อนลงนาม — สถานศึกษาไม่สามารถแก้ไขผลการประมวลผลได้เอง
          </div>
        </v-card-text>
      </v-card>
    </template>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="5000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">ปิด</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

const API = "govem_compensation_college.php";

export default {
  name: "CompensationResultView",

  data() {
    return {
      ApiKey: "HRvec2021",
      loading: false,
      loaded: false,
      uploading: false,
      downloading: false,
      years: [],
      compYear: null,
      unit: null,
      run: null,
      summary: null,
      details: [],
      cert: null,
      filterMode: "all",
      search: "",
      form: { director_name: "", college_note: "", file: null },
      snackbar: { show: false, color: "success", text: "" },
      headers: [
        { text: "#", value: "order", width: 40 },
        { text: "ชื่อ-สกุล / ตำแหน่ง", value: "full_name" },
        { text: "สิทธิ์", value: "eligible", width: 150 },
        { text: "ค่าตอบแทนเดิม", value: "salary_s", align: "right" },
        { text: "คะแนน", value: "performance_score", align: "center" },
        { text: "% รายบุคคล", value: "individual_pct", align: "center" },
        { text: "% บริหารวงเงิน", value: "management_pct", align: "center" },
        { text: "% เลื่อนจริงก่อนปัดเศษ", value: "actual_pct_raw", align: "center" },
        { text: "", value: "id_card", align: " d-none" },
        { text: "เลื่อนจริงก่อนปัดเศษ (บาท)", value: "actual_increase_raw", align: "right" },
        { text: "เลื่อนจริง (บาท)", value: "final_rounded", align: "right" },
        { text: "% เลื่อนจริง", value: "actual_pct", align: "center" },
        { text: "ค่าตอบแทนใหม่", value: "new_salary", align: "right" },
        { text: "", value: "name_postion", align: " d-none" }
      ]
    };
  },

  computed: {
    eligibleCount() {
      return this.details.filter(d => +d.eligible).length;
    },
    filteredDetails() {
      let list = this.details;
      if (this.filterMode === "eligible") list = list.filter(d => +d.eligible);
      if (this.filterMode === "ineligible") list = list.filter(d => !+d.eligible);
      return list.map((d, i) => ({ ...d, order: i + 1 }));
    },
    totalRaw() {
      return this.details.reduce((s, d) => s + (+d.eligible ? +d.actual_increase_raw : 0), 0);
    },
    steps() {
      const c = this.cert && this.cert.cert_status;
      const hasRun = !!this.run;
      return [
        { text: "ส่วนกลางประมวลผลและยืนยันผล", icon: "mdi-calculator", done: hasRun },
        { text: "หน่วยงานตรวจสอบ", icon: "mdi-magnify", done: hasRun && !!c, current: hasRun && !c },
        { text: "พิมพ์แบบรายงาน ผอ.ลงนาม", icon: "mdi-printer", done: !!c && c !== "rejected", current: c === "rejected" },
        { text: "แนบไฟล์ผ่านระบบ", icon: "mdi-upload", done: !!c && c !== "rejected", current: c === "rejected" },
        { text: "ส่วนกลางรับรอง", icon: "mdi-check-decagram", done: c === "accepted", current: c === "submitted" }
      ];
    },
    kpis() {
      const s = this.summary;
      return [
        { label: "พนักงานราชการ", value: this.money(s.personnel_count), unit: "คน", color: "#1565c0" },
        { label: "มีสิทธิ์รับการเลื่อน", value: this.money(s.eligible_count), unit: `ไม่มีสิทธิ์ ${this.money(s.ineligible_count)} คน`, color: "#2e7d32" },
        { label: "ฐานค่าตอบแทนรวม", value: this.money(s.total_salary), unit: "บาท/เดือน", color: "#455a64" },
        { label: `วงเงิน ${this.run.budget_pct}%`, value: this.money(s.budget_year), unit: "บาท/ปี", color: "#ef6c00" },
        { label: "เลื่อนจริงรวม", value: this.money(s.distributed_month), unit: `บาท/เดือน (${this.money(s.distributed_year)} บาท/ปี)`, color: "#6a1b9a" },
        { label: "วงเงินคงเหลือ", value: this.money(s.budget_year - s.distributed_year), unit: "บาท/ปี", color: "#00838f" }
      ];
    }
  },

  mounted() {
    this.load();
  },

  methods: {
    money(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", { maximumFractionDigits: 0 });
    },
    money2(v) {
      if (v === null || v === undefined || isNaN(v)) return "-";
      return Number(v).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    pct(item, field) {
      return +item.eligible ? Number(item[field]).toFixed(3) + "%" : "-";
    },
    roundModeText(v) {
      return { latest: "รอบล่าสุด", 1: "รอบที่ 1", 2: "รอบที่ 2" }[v] || v;
    },
    certText(s) {
      return { submitted: "ส่งแล้ว รอส่วนกลางตรวจ", accepted: "ส่วนกลางรับรองแล้ว", rejected: "ส่งกลับแก้ไข" }[s] || s;
    },
    certColor(s) {
      return { submitted: "info", accepted: "success", rejected: "error" }[s] || "grey";
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    errMsg(err) {
      return (err.response && err.response.data && err.response.data.message) || err.message;
    },

    async load() {
      this.loading = true;
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, action: "get", comp_year: this.compYear });
        if (!res.data.status) throw new Error(res.data.message);
        const d = res.data;
        this.years = d.years;
        this.unit = d.unit;
        this.run = d.run;
        this.summary = d.summary || null;
        this.details = d.details || [];
        this.cert = d.cert || null;
        if (d.run) this.compYear = d.run.comp_year;
        this.form.director_name =
          (this.cert && this.cert.director_name) || (this.unit && this.unit.user_director) || "";
      } catch (e) {
        this.notify("โหลดข้อมูลไม่สำเร็จ: " + this.errMsg(e), "error");
      } finally {
        this.loading = false;
        this.loaded = true;
      }
    },

    async uploadCert() {
      const fd = new FormData();
      fd.append("ApiKey", this.ApiKey);
      fd.append("action", "upload_cert");
      fd.append("run_id", this.run.run_id);
      fd.append("director_name", this.form.director_name);
      fd.append("college_note", this.form.college_note || "");
      fd.append("cert_file", this.form.file);
      this.uploading = true;
      try {
        const res = await this.$http.post(API, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (!res.data.status) throw new Error(res.data.message);
        this.cert = res.data.cert;
        this.form.file = null;
        this.notify("ส่งแบบรายงานเรียบร้อย รอส่วนกลางตรวจสอบ");
      } catch (e) {
        this.notify(this.errMsg(e), "error");
      } finally {
        this.uploading = false;
      }
    },

    async openCert() {
      this.downloading = true;
      try {
        const res = await this.$http.post(
          API,
          { ApiKey: this.ApiKey, action: "download_cert", run_id: this.run.run_id },
          { responseType: "blob" }
        );
        if ((res.headers["content-type"] || "").includes("application/json")) {
          const j = JSON.parse(await res.data.text());
          throw new Error(j.message);
        }
        window.open(URL.createObjectURL(res.data), "_blank");
      } catch (e) {
        this.notify("เปิดไฟล์ไม่สำเร็จ: " + this.errMsg(e), "error");
      } finally {
        this.downloading = false;
      }
    },

    exportExcel() {
      const rows = this.details.map((d, i) => ({
        ลำดับ: i + 1,
        เลขบัตรประชาชน: d.id_card,
        "ชื่อ-สกุล": d.full_name,
        ตำแหน่ง: d.name_postion,
        กลุ่มงาน: d.name_type_group,
        ค่าตอบแทนเดิม: +d.salary_s,
        คะแนนประเมิน: +d.performance_score,
        รอบประเมิน: d.score_round || "",
        สิทธิ์: +d.eligible ? "มีสิทธิ์" : "ไม่มีสิทธิ์",
        หมายเหตุ: d.ineligible_reason || "",
        "% รายบุคคล": +d.individual_pct,
        "% บริหารวงเงิน": +d.management_pct,
        "% เลื่อนจริงก่อนปัดเศษ": +d.actual_pct_raw,
        "เลื่อนจริงก่อนปัดเศษ (บาท)": +d.actual_increase_raw,
        "เลื่อนจริง (บาท)": +d.final_rounded,
        "% เลื่อนจริง": +d.actual_pct,
        ค่าตอบแทนใหม่: +d.new_salary,
        ถึงขั้นสูงกลุ่มงาน: +d.capped ? "ใช่" : "",
        ตัดที่เพดาน6: +d.pct_capped ? "ใช่" : ""
      }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "เลื่อนค่าตอบแทน");
      XLSX.writeFile(wb, `เลื่อนค่าตอบแทน_${this.unit.user_code}_${this.run.comp_year}.xlsx`);
    },

    // แบบรายงานผลการเลื่อนค่าตอบแทน สำหรับผู้อำนวยการลงนามรับรอง แล้วสแกนแนบกลับเข้าระบบ
    printForm() {
      const esc = s => String(s === null || s === undefined ? "" : s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      const m = v => this.money(v);
      const m2 = v => this.money2(v);
      const p3 = v => Number(v).toFixed(3);
      const s = this.summary;
      const rows = this.details.map((d, i) => {
        const e = +d.eligible;
        const note = [d.ineligible_reason, +d.capped ? "ถึงขั้นสูงกลุ่มงาน" : "", +d.pct_capped ? "ตัดที่เพดาน 6%" : ""]
          .filter(Boolean).join(" ");
        return `<tr><td>${i + 1}</td><td class="l">${esc(d.full_name)}<br><span class="sm">${esc(d.id_card)}</span></td>
          <td class="l">${esc(d.name_postion)}</td><td>${m(d.salary_s)}</td>
          <td>${+d.performance_score > 0 ? esc(d.performance_score) : "-"}</td>
          <td>${e ? p3(d.actual_pct_raw) : "-"}</td><td>${e ? m2(d.actual_increase_raw) : "-"}</td>
          <td><b>${e ? m(d.final_rounded) : "-"}</b></td><td>${e ? p3(d.actual_pct) : "-"}</td>
          <td><b>${m(d.new_salary)}</b></td><td class="l sm">${esc(note)}</td></tr>`;
      }).join("");
      const director = esc(this.form.director_name || "......................................................");
      const html = `<!doctype html><html><head><meta charset="utf-8"><title>แบบรายงานผลการเลื่อนค่าตอบแทน ${esc(this.unit.college_name)}</title>
        <style>
          @page{size:A4 landscape;margin:12mm}
          body{font-family:"TH Sarabun New","Sarabun",sans-serif;font-size:14px;color:#000}
          h2{text-align:center;margin:0;font-size:19px} .c{text-align:center}
          table{border-collapse:collapse;width:100%;margin-top:8px} th,td{border:1px solid #333;padding:2px 4px;text-align:right;vertical-align:top}
          th{background:#eee;text-align:center} td.l{text-align:left} .sm{font-size:11px;color:#333}
          tfoot td{font-weight:bold;background:#f3f3f3} thead{display:table-header-group}
          .cert{margin-top:14px;line-height:1.7} .signs{display:flex;justify-content:space-around;margin-top:28px;text-align:center;line-height:2}
        </style></head><body>
        <h2>แบบรายงานผลการเลื่อนค่าตอบแทนพนักงานราชการ ประจำปีงบประมาณ ${esc(this.run.comp_year)}</h2>
        <div class="c">${esc(this.unit.college_name)} (${esc(this.unit.user_code)}) ${this.unit.province_name ? "จังหวัด" + esc(this.unit.province_name) : ""}</div>
        <div class="c">มีผลตั้งแต่วันที่ 1 ตุลาคม ${esc(this.run.comp_year)} · ประมวลผลโดยสำนักงานคณะกรรมการการอาชีวศึกษา (รอบ #${esc(this.run.run_id)} ยืนยันผล ${esc(this.run.finalized_at)})</div>
        <div class="c">พนักงาน ${m(s.personnel_count)} คน · มีสิทธิ์ ${m(s.eligible_count)} คน · ฐานค่าตอบแทนรวม ${m(s.total_salary)} บาท/เดือน ·
          วงเงิน ${esc(this.run.budget_pct)}% ${m(s.budget_year)} บาท/ปี · เลื่อนจริง ${m(s.distributed_month)} บาท/เดือน (${m(s.distributed_year)} บาท/ปี)</div>
        <table><thead><tr><th>ที่</th><th>ชื่อ-สกุล</th><th>ตำแหน่ง</th><th>ค่าตอบแทนเดิม</th><th>คะแนน</th>
          <th>% เลื่อนก่อนปัดเศษ</th><th>เลื่อนก่อนปัดเศษ (บาท)</th><th>เลื่อนจริง (บาท)</th><th>% เลื่อนจริง</th><th>ค่าตอบแทนใหม่</th><th>หมายเหตุ</th></tr></thead>
          <tbody>${rows}</tbody>
          <tfoot><tr><td colspan="3" class="l">รวม</td><td>${m(s.total_salary)}</td><td></td><td></td><td>${m2(this.totalRaw)}</td>
          <td>${m(s.distributed_month)}</td><td></td><td>${m(s.new_salary_total)}</td><td></td></tr></tfoot></table>
        <div class="cert">ขอรับรองว่า ${esc(this.unit.college_name)} ได้ตรวจสอบผลการเลื่อนค่าตอบแทนพนักงานราชการข้างต้น
          ซึ่งสำนักงานคณะกรรมการการอาชีวศึกษาเป็นผู้ประมวลผล ตามรายชื่อ ผลการประเมิน และค่าตอบแทนที่ปรากฏในระบบแล้ว ถูกต้องครบถ้วน</div>
        <div class="signs">
          <div>(ลงชื่อ)......................................................ผู้จัดทำ/ผู้ตรวจสอบ<br>(......................................................)<br>ตำแหน่ง......................................................</div>
          <div>(ลงชื่อ)......................................................ผู้รับรอง<br>(${director})<br>ผู้อำนวยการ${esc(this.unit.college_name)}<br>วันที่........./................../..............</div>
        </div></body></html>`;
      const w = window.open("", "_blank");
      if (!w) {
        this.notify("เบราว์เซอร์บล็อกหน้าต่างใหม่ กรุณาอนุญาต pop-up", "warning");
        return;
      }
      w.document.write(html);
      w.document.close();
      w.focus();
      w.print();
    }
  }
};
</script>

<style scoped>
.kpi {
  background: #fff;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
  border-left: 4px solid #455a64;
  height: 100%;
}
.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #777;
}
.kpi-value {
  font-size: 19px;
  font-weight: bold;
  margin: 4px 0;
}
.kpi-unit {
  font-size: 11px;
  color: #999;
}
.calc-table >>> th {
  white-space: nowrap;
}
</style>
